import apiClient from '../utils/axios-config';
import { logger } from '../utils/logger';
import { FollowStatus, FollowResult, UserList, FollowRequestList } from '../types/follow';
import ActionTracker from './action-tracker';
import enhancedAuthService from './enhanced-auth-service';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev' || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

/**
 * Service for follow-related API calls
 */
export class FollowService {
  private async getAuthHeaders() {
    const token = await enhancedAuthService.getAccessToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Follow a user
   * @param userId ID of the user to follow
   * @returns Follow result
   */
  async followUser(userId: string): Promise<FollowResult> {
    try {
      const response = await apiClient.post(
        `${API_BASE_URL}/social/follow`,
        {
          action: 'follow',
          targetUserId: userId
        },
        {
          headers: await this.getAuthHeaders(),
        }
      );

      // Track action for discovery algorithm
      ActionTracker.trackFollow(userId, true);
      
      logger.info(`Successfully followed user: ${userId}`, response.data);
      return {
        success: response.data.success,
        requiresApproval: response.data.requiresApproval,
        requestId: response.data.requestId,
        message: response.data.message,
        status: response.data.data?.status || (response.data.requiresApproval ? 'pending' : 'following'),
      };
    } catch (error: any) {
      logger.error(`Error following user ${userId}:`, error);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Failed to follow user');
      }
      
      throw new Error('Failed to follow user');
    }
  }

  /**
   * Unfollow a user
   * @param userId ID of the user to unfollow
   */
  async unfollowUser(userId: string): Promise<void> {
    try {
      await apiClient.post(
        `${API_BASE_URL}/social/follow`,
        {
          action: 'unfollow',
          targetUserId: userId
        },
        {
          headers: await this.getAuthHeaders(),
        }
      );

      // Track action for discovery algorithm
      ActionTracker.trackFollow(userId, false);
      
      logger.info(`Successfully unfollowed user: ${userId}`);
    } catch (error: any) {
      logger.error(`Error unfollowing user ${userId}:`, error);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Failed to unfollow user');
      }
      
      throw new Error('Failed to unfollow user');
    }
  }

  /**
   * Check follow status between current user and target user
   * @param userId ID of the target user
   * @returns Follow status
   */
  async getFollowStatus(userId: string): Promise<FollowStatus> {
    try {
      // Check if user is authenticated first
      const isAuthenticated = await enhancedAuthService.isAuthenticated();
      if (!isAuthenticated) {
        logger.info('User not authenticated for follow status check - returning default status');
        return {
          isFollowing: false,
          isFollowedBy: false,
          relationship: 'none',
        };
      }

      const response = await apiClient.post(
        `${API_BASE_URL}/social/follow`,
        {
          action: 'follow-status',
          targetUserId: userId
        },
        {
          headers: await this.getAuthHeaders(),
        }
      );

      return {
        isFollowing: response.data.isFollowing,
        isFollowedBy: response.data.isFollowedBy,
        requestStatus: response.data.requestStatus,
        relationship: response.data.relationship,
      };
    } catch (error: any) {
      // Handle 401 errors gracefully without logging as errors
      if (error.response?.status === 401) {
        logger.info(`User not authenticated for follow status check for user ${userId} - returning default status`);
        return {
          isFollowing: false,
          isFollowedBy: false,
          relationship: 'none',
        };
      }
      
      logger.error(`Error getting follow status for user ${userId}:`, error);
      
      // Return default status if error
      return {
        isFollowing: false,
        isFollowedBy: false,
        relationship: 'none',
      };
    }
  }

  /**
   * Get followers of a user
   * @param userId ID of the user
   * @param limit Number of followers to fetch (from backend API, default: 20)
   * @param offset Offset for pagination (default: 0)
   * @returns List of followers
   */
  
  /**
   * Get users that follow a user
   * @param userId ID of the user
   * @param limit Number of followers to fetch (from backend API, default: 20)
   * @param offset Offset for pagination (default: 0)
   * @returns List of followers
   */
    async getFollowers(userId: string, limit: number = 20, offset: number = 0): Promise<UserList> {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/${userId}/followers`, {
        params: { limit, offset },
        headers: await this.getAuthHeaders(),
      });

      // Fix: API returns data.followers but frontend expects users
      // Also fix pagination mapping
      return {
        users: response.data.data?.followers || [], // Map followers to users
        pagination: {
          limit: response.data.pagination?.limit || limit,
          offset: response.data.pagination?.offset || offset,
          total: response.data.data?.total || 0, // Get total from data, not pagination
          hasMore: response.data.pagination?.hasMore || false,
          nextOffset: (response.data.pagination?.offset || offset) + (response.data.pagination?.limit || limit)
        },
        meta: response.data.meta || {},
      };
    } catch (error: any) {
      logger.error(`Error getting followers for user ${userId}:`, error);
      throw new Error('Failed to get followers');
    }
  }
  /**
   * Get users that a user is following
   * @param userId ID of the user
   * @param limit Number of following to fetch (from backend API, default: 20)
   * @param offset Offset for pagination (default: 0)
   * @returns List of users being followed
   */
  
  /**
   * Get users that a user is following
   * @param userId ID of the user
   * @param limit Number of following to fetch (from backend API, default: 20)
   * @param offset Offset for pagination (default: 0)
   * @returns List of users being followed
   */
    async getFollowing(userId: string, limit: number = 20, offset: number = 0): Promise<UserList> {
    try {
      // Use the correct API endpoint that exists in API Gateway
      const response = await apiClient.get(`${API_BASE_URL}/users/${userId}/following`, {
        params: { limit, offset },
        headers: await this.getAuthHeaders(),
      });

      // Fix: API returns data.following but frontend expects users
      // Also fix pagination mapping
      return {
        users: response.data.data?.following || [], // Map following to users
        pagination: {
          limit: response.data.pagination?.limit || limit,
          offset: response.data.pagination?.offset || offset,
          total: response.data.data?.total || 0, // Get total from data, not pagination
          hasMore: response.data.pagination?.hasMore || false,
          nextOffset: (response.data.pagination?.offset || offset) + (response.data.pagination?.limit || limit)
        },
        meta: response.data.meta || {},
      };
    } catch (error: any) {
      logger.error(`Error getting following for user ${userId}:`, error);
      throw new Error('Failed to get following');
    }
  }
  /**
   * Get follow requests for the current user
   * @param limit Number of requests to fetch (default: 20)
   * @param offset Offset for pagination (default: 0)
   * @param status Status filter for requests (default: 'pending')
   * @returns List of follow requests
   */
  async getFollowRequests(
    limit: number = 20, 
    offset: number = 0, 
    status: string = 'pending'
  ): Promise<FollowRequestList> {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/me/follow-requests`, {
        params: { limit, offset, status },
        headers: await this.getAuthHeaders(),
      });

      return {
        requests: response.data.requests,
        pagination: response.data.pagination,
      };
    } catch (error: any) {
      logger.error('Error getting follow requests:', error);
      throw new Error('Failed to get follow requests');
    }
  }

  /**
   * Approve a follow request
   * @param requestId ID of the follow request
   */
  async approveFollowRequest(requestId: string): Promise<void> {
    try {
      await apiClient.post(`${API_BASE_URL}/users/me/follow-requests/${requestId}/approve`, {}, {
        headers: await this.getAuthHeaders(),
      });

      logger.info(`Successfully approved follow request: ${requestId}`);
    } catch (error: any) {
      logger.error(`Error approving follow request ${requestId}:`, error);
      throw new Error('Failed to approve follow request');
    }
  }

  /**
   * Deny a follow request
   * @param requestId ID of the follow request
   */
  async denyFollowRequest(requestId: string): Promise<void> {
    try {
      await apiClient.post(`${API_BASE_URL}/users/me/follow-requests/${requestId}/deny`, {}, {
        headers: await this.getAuthHeaders(),
      });

      logger.info(`Successfully denied follow request: ${requestId}`);
    } catch (error: any) {
      logger.error(`Error denying follow request ${requestId}:`, error);
      throw new Error('Failed to deny follow request');
    }
  }

  /**
   * Search for users by username or display name
   * @param query Search query
   * @param limit Number of results to return (default: 20)
   * @returns List of matching users
   */
  async searchUsers(query: string, limit: number = 20): Promise<UserList['users']> {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/search`, {
        params: { q: query, limit },
        headers: await this.getAuthHeaders(),
      });

      return response.data.users || [];
    } catch (error: any) {
      logger.error(`Error searching users with query "${query}":`, error);
      return [];
    }
  }

  /**
   * Block a user
   * @param userId ID of the user to block
   */
  async blockUser(userId: string): Promise<void> {
    try {
      await apiClient.post(`${API_BASE_URL}/users/${userId}/block`, {}, {
        headers: await this.getAuthHeaders(),
      });

      logger.info(`Successfully blocked user: ${userId}`);
    } catch (error: any) {
      logger.error(`Error blocking user ${userId}:`, error);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Failed to block user');
      }
      
      throw new Error('Failed to block user');
    }
  }

  /**
   * Unblock a user
   * @param userId ID of the user to unblock
   */
  async unblockUser(userId: string): Promise<void> {
    try {
      await apiClient.delete(`${API_BASE_URL}/users/${userId}/block`, {
        headers: await this.getAuthHeaders(),
      });

      logger.info(`Successfully unblocked user: ${userId}`);
    } catch (error: any) {
      logger.error(`Error unblocking user ${userId}:`, error);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Failed to unblock user');
      }
      
      throw new Error('Failed to unblock user');
    }
  }

  /**
   * Check block status between current user and target user
   * @param userId ID of the target user
   * @returns Block status
   */
  async getBlockStatus(userId: string): Promise<{ isBlocked: boolean; isBlockedBy: boolean }> {
    try {
      // Check if user is authenticated first
      const isAuthenticated = await enhancedAuthService.isAuthenticated();
      if (!isAuthenticated) {
        logger.info('User not authenticated for block status check - returning default status');
        return {
          isBlocked: false,
          isBlockedBy: false,
        };
      }

      const response = await apiClient.get(`${API_BASE_URL}/users/${userId}/block-status`, {
        headers: await this.getAuthHeaders(),
      });

      return {
        isBlocked: response.data.data.isBlocked,
        isBlockedBy: response.data.data.isBlockedBy,
      };
    } catch (error: any) {
      // Handle 401 errors gracefully without logging as errors
      if (error.response?.status === 401) {
        logger.info(`User not authenticated for block status check for user ${userId} - returning default status`);
        return {
          isBlocked: false,
          isBlockedBy: false,
        };
      }
      
      logger.error(`Error getting block status for user ${userId}:`, error);
      
      // Return default status if error
      return {
        isBlocked: false,
        isBlockedBy: false,
      };
    }
  }

  /**
   * Get blocked users for the current user
   * @param pagination Pagination parameters
   * @returns List of blocked users
   */
  async getBlockedUsers(pagination: { limit: number; offset: number }): Promise<UserList> {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/me/blocked`, {
        params: pagination,
        headers: await this.getAuthHeaders(),
      });

      return {
        users: response.data.data.users,
        pagination: response.data.data.pagination,
        meta: {
          targetUserId: '',
          timestamp: new Date().toISOString(),
          cached: false,
        },
      };
    } catch (error: any) {
      logger.error('Error getting blocked users:', error);
      throw new Error('Failed to get blocked users');
    }
  }

  /**
   * Update privacy settings
   * @param isPrivate Whether the account should be private
   */
  async updatePrivacySettings(isPrivate: boolean): Promise<void> {
    try {
      await apiClient.put(`${API_BASE_URL}/users/me/privacy`, 
        { isPrivate },
        {
          headers: await this.getAuthHeaders(),
        }
      );

      logger.info(`Successfully updated privacy settings: isPrivate=${isPrivate}`);
    } catch (error: any) {
      logger.error(`Error updating privacy settings:`, error);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Failed to update privacy settings');
      }
      
      throw new Error('Failed to update privacy settings');
    }
  }
}

const followService = new FollowService();

// Export convenience functions for easier use in components
export const followUser = (userId: string) => followService.followUser(userId);
export const unfollowUser = (userId: string) => followService.unfollowUser(userId);
export const getFollowStatus = (userId: string) => followService.getFollowStatus(userId);
export const getFollowers = (userId: string, limit?: number, offset?: number) => 
  followService.getFollowers(userId, limit, offset);
export const getFollowing = (userId: string, limit?: number, offset?: number) => 
  followService.getFollowing(userId, limit, offset);
export const getFollowRequests = (limit?: number, offset?: number, status?: string) => 
  followService.getFollowRequests(limit, offset, status);
export const approveFollowRequest = (requestId: string) => followService.approveFollowRequest(requestId);
export const denyFollowRequest = (requestId: string) => followService.denyFollowRequest(requestId);
export const searchUsers = (query: string, limit?: number) => followService.searchUsers(query, limit);
export const blockUser = (userId: string) => followService.blockUser(userId);
export const unblockUser = (userId: string) => followService.unblockUser(userId);
export const getBlockStatus = (userId: string) => followService.getBlockStatus(userId);
export const getBlockedUsers = (pagination: { limit: number; offset: number }) => 
  followService.getBlockedUsers(pagination);
export const updatePrivacySettings = (isPrivate: boolean) => followService.updatePrivacySettings(isPrivate);

export default followService;